// src/components/ui/BoutonRafraichir.tsx — SCE Issues
// Bouton « Rafraîchir les données » : fiche problème et pages principales.
//
// Pas de temps réel (Static Web App, pas de backend) : un coordinateur peut
// qualifier, clore ou modifier pendant qu'un autre a la page ouverte. Ce
// bouton relit sans recharger l'application — filtres, recherche et
// défilement restent en place.

import { useState, type JSX } from 'react'
import { RefreshCw } from 'lucide-react'
import { useSnackbar } from '@/components/ui/Snackbar'
import { rafraichirProblemes } from '@/hooks/signalProblemesModifies'
import { Cloche } from '@/components/notifications/Cloche'

/** Le bouton seul. Icône qui tourne pendant la relecture, 44 px de cible. */
export function BoutonRafraichir({
  enCours,
  onClick,
}: {
  enCours: boolean
  onClick: () => void
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={enCours}
      aria-label="Rafraîchir les données"
      title="Rafraîchir les données"
      className="flex items-center justify-center flex-shrink-0"
      style={{
        width: 44,
        height: 44,
        borderRadius: 22,
        border: 'none',
        background: 'transparent',
        color: 'var(--color-black)',
        cursor: enCours ? 'default' : 'pointer',
      }}
    >
      <RefreshCw size={18} className={enCours ? 'animate-spin' : undefined} />
    </button>
  )
}

/**
 * Pour les pages principales : relit TOUTES les listes de problèmes montées
 * (cf. signalProblemesModifies) — la page n'a rien à câbler. En cas d'échec,
 * la page garde ses données précédentes et on le dit.
 */
export function BoutonRafraichirProblemes(): JSX.Element {
  const [enCours, setEnCours] = useState(false)
  const { show } = useSnackbar()

  async function rafraichir(): Promise<void> {
    setEnCours(true)
    try {
      const ok = await rafraichirProblemes()
      if (!ok) show('Rafraîchissement impossible — les données affichées sont les précédentes.', 'error')
    } finally {
      setEnCours(false)
    }
  }

  // La cloche (03/11) vit à droite de Rafraîchir, qui la relit aussi
  // (ChargeurCloche écoute le même signal) : chaque page qui a l'un a l'autre.
  return (
    <>
      <BoutonRafraichir enCours={enCours} onClick={() => void rafraichir()} />
      <Cloche />
    </>
  )
}
